import * as React from 'react'
import { gsap } from 'gsap'

import { useHasRendered } from './use-has-rendered'
import { useMouseTracker } from './use-mouse-tracker'

export const useLantern = <T extends HTMLElement>({
  size,
  isFocused,
}: {
  size: 'sm' | 'md' | 'lg'
  isFocused?: boolean
}) => {
  const maskRef = React.useRef<T>(null)
  const [isFocusing, setIsFocusing] = React.useState(isFocused ?? false)

  // don't ask me why, but this extra rerender is making the animation work
  // when we migrated to the appDir (https://github.com/basementstudio/basehub/pull/43)
  // the animation stopped working, and this is the only thing that fixed it
  // i belive its because the animation needed to rerender (cause its badly programmed)
  // and the appDir is actually saving us some rerenders, but also making this animation not work?
  // TODO: fix this
  useHasRendered()

  const sizes = React.useMemo(() => {
    if (size === 'sm') {
      return {
        '--radius-x': isFocusing ? '190%' : '30%',
        '--radius-y': isFocusing ? '800px' : '30px',
        '--opacity': isFocusing ? 1 : 0,
      }
    }

    if (size === 'md') {
      return {
        '--radius-x': isFocusing ? '290%' : '80%',
        '--radius-y': isFocusing ? '1800px' : '80px',
        '--opacity': isFocusing ? 1 : 0,
      }
    }

    if (size === 'lg') {
      return {
        '--radius-x': isFocusing ? '390%' : '130%',
        '--radius-y': isFocusing ? '2800px' : '130px',
        '--opacity': isFocusing ? 1 : 0,
      }
    }
  }, [size, isFocusing])

  const { elementRef } = useMouseTracker(
    React.useMemo(() => {
      return {
        onChange: ({ x, y, isHovering, first }) => {
          if (!maskRef.current) return
          if (isFocusing) return
          const webkitMaskImage = `radial-gradient(var(--radius-x) var(--radius-y) at ${x}px ${y}px, black 45%,transparent)`
          if (first) {
            gsap.set(maskRef.current, {
              webkitMaskImage,
            })
            gsap.to(maskRef.current, {
              ...sizes,
              '--opacity': isHovering ? 1 : 0,
              duration: 0.5,
            })
          } else {
            gsap.to(maskRef.current, {
              ...sizes,
              '--opacity': isHovering ? 1 : 0,
              webkitMaskImage,
              duration: 0.5,
            })
          }
        },
        windowAsProxy: true,
        // enableOnlyWhenHovering: true,
      }
    }, [isFocusing, sizes])
  )

  React.useEffect(() => {
    if (!maskRef.current) return
    const tween = gsap.to(maskRef.current, {
      ...sizes,
    })

    return () => {
      tween.kill()
    }
  }, [sizes])

  return {
    maskRef,
    elementRef,
    focusHandlers: {
      onFocus: () => setIsFocusing(true),
      onBlur: () => setIsFocusing(false),
    },
  }
}
